import {
  EnvironmentInjector,
  Injectable,
  createEnvironmentInjector,
  inject,
} from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import {
  Observable,
  TimeoutError,
  catchError,
  filter,
  map,
  throwError,
  timeout,
} from 'rxjs';
import { AuthInterceptor } from 'src/app/interceptors/auth.interceptor';
import { BaseUrlInterceptor } from 'src/app/interceptors/base-url.interceptor';
import { SUPPORT_API } from '../support-system.config';
import {
  IssueReportNudgesResponse,
  IssueReportRequest,
  IssueReportResponse,
  SupportApiAttachmentReservation,
  SupportApiCatchUp,
  SupportApiChat,
  SupportApiEnvelope,
  SupportApiMe,
  SupportApiMessage,
  SupportApiPage,
  SupportApiSendResult,
  SupportApiSignedUrl,
  SupportStoredDiagnostics,
  SupportErrorCode,
  SupportInboxFilters,
} from '../support-system.models';

export class SupportApiError extends Error {
  constructor(
    readonly code: SupportErrorCode,
    message: string,
  ) {
    super(message);
  }
}

const STATUS_CODES: Record<number, SupportErrorCode> = {
  400: 'INVALID_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  429: 'RATE_LIMITED',
  503: 'UNAVAILABLE',
};

/** Any failure (HTTP, envelope, timeout, socket ack) as a SupportApiError. */
export function toSupportError(error: unknown): SupportApiError {
  if (error instanceof SupportApiError) return error;
  if (error instanceof TimeoutError) {
    return new SupportApiError('TIMEOUT', 'No answer from the support server');
  }
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return new SupportApiError('OFFLINE', 'Support server unreachable');
    }
    const body = error.error?.error;
    if (body?.code) return new SupportApiError(body.code, body.message || '');
    return new SupportApiError(
      STATUS_CODES[error.status] || 'SERVER_ERROR',
      error.message,
    );
  }
  return new SupportApiError('SERVER_ERROR', String(error));
}

/** The `data` of a successful envelope; throws its error otherwise. */
export function unwrapEnvelope<T>(envelope: SupportApiEnvelope<T>): T {
  if (envelope?.success) return envelope.data;
  const error = envelope && !envelope.success ? envelope.error : null;
  throw new SupportApiError(
    error?.code || 'SERVER_ERROR',
    error?.message || 'Unexpected response',
  );
}

/**
 * REST client for `/v1/support`. REST is the source of truth; the socket
 * only announces changes.
 */
@Injectable({ providedIn: 'root' })
export class SupportApiService {
  /**
   * Own HttpClient with only the base-URL and auth interceptors, like Team
   * Dashboard. The app-wide chain adds query params (rejected by the
   * backend's strict validation) and logs users out on errors.
   */
  private readonly http = createEnvironmentInjector(
    [provideHttpClient(withInterceptors([BaseUrlInterceptor, AuthInterceptor]))],
    inject(EnvironmentInjector),
  ).get(HttpClient);
  /**
   * No interceptors at all, for the signed storage URLs: the dashboard token
   * and base URL must never reach Azure.
   */
  private readonly storageHttp = new HttpClient(inject(HttpBackend));

  me(): Observable<SupportApiMe> {
    return this.get('/me');
  }

  /** The open chat for this target, created if there is none. */
  openChat(target?: string): Observable<{ chat: SupportApiChat; created: boolean }> {
    return this.post('/chats', target ? { target } : {});
  }

  /** The user's chats (open and closed), newest first. */
  myChats(): Observable<SupportApiPage<SupportApiChat>> {
    return this.get('/chats/mine', { limit: SUPPORT_API.pageSize });
  }

  /** Team inbox, latest activity first (managers and CS). */
  inbox(filters: SupportInboxFilters = {}): Observable<SupportApiPage<SupportApiChat>> {
    const params: Record<string, string | number> = { limit: SUPPORT_API.pageSize };
    for (const [key, value] of Object.entries(filters)) {
      if (value) params[key] = value;
    }
    return this.get('/chats', params);
  }

  /** Takes an unassigned chat over (managers). 409 if someone was faster. */
  claim(chatId: string): Observable<SupportApiChat> {
    return this.post(`/chats/${chatId}/claim`, {});
  }

  /** Closes a chat (its assigned manager); read-only afterwards. */
  close(chatId: string): Observable<SupportApiChat> {
    return this.post(`/chats/${chatId}/close`, {});
  }

  chat(chatId: string): Observable<SupportApiChat> {
    return this.get(`/chats/${chatId}`);
  }

  /** Newest page, oldest first; `nextCursor` loads the page before it. */
  messages(
    chatId: string,
    cursor?: string | null,
  ): Observable<SupportApiPage<SupportApiMessage>> {
    const params: Record<string, string | number> = {
      limit: SUPPORT_API.pageSize,
    };
    if (cursor) params['cursor'] = cursor;
    return this.get(`/chats/${chatId}/messages`, params);
  }

  /**
   * The diagnostics snapshot of an issue report (team only). Load it when
   * the viewer opens and don't keep it: it can hold shop and customer data.
   */
  diagnostics(
    chatId: string,
    messageId: string,
  ): Observable<SupportStoredDiagnostics> {
    return this.get(`/chats/${chatId}/messages/${messageId}/diagnostics`);
  }

  /** Everything after a known message (catch-up after a reconnect). */
  messagesAfter(chatId: string, afterId: string): Observable<SupportApiCatchUp> {
    return this.get(`/chats/${chatId}/messages`, { after: afterId, limit: 100 });
  }

  /**
   * Safe to repeat with the same clientMessageId (and the same attachment
   * ids): no duplicate is created.
   */
  sendMessage(
    chatId: string,
    body: string,
    clientMessageId: string,
    attachmentIds: string[] = [],
  ): Observable<SupportApiSendResult> {
    // Optional fields only when set: the validation is strict.
    const payload: Record<string, unknown> = { body, clientMessageId };
    if (attachmentIds.length) payload['attachmentIds'] = attachmentIds;
    return this.post(`/chats/${chatId}/messages`, payload);
  }

  /** Step 1 of an upload: a one-time URL to PUT this file to. */
  reserveAttachment(
    chatId: string,
    file: File,
  ): Observable<SupportApiAttachmentReservation> {
    return this.post(`/chats/${chatId}/attachments`, {
      name: file.name,
      size: file.size,
      mimeType: file.type,
    });
  }

  /** Step 2: PUT the file to storage. Emits the share uploaded (0–1). */
  uploadToStorage(
    upload: SupportApiAttachmentReservation['upload'],
    file: File,
  ): Observable<number> {
    return this.storageHttp
      .request(upload.method, upload.url, {
        body: file,
        headers: new HttpHeaders(upload.headers),
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        filter(
          (event) =>
            event.type === HttpEventType.UploadProgress ||
            event.type === HttpEventType.Response,
        ),
        map((event) =>
          // 1 only once storage has answered, not when the last byte left.
          event.type === HttpEventType.UploadProgress
            ? Math.min(event.loaded / (event.total || file.size || 1), 0.99)
            : 1,
        ),
        catchError(() =>
          throwError(() => new SupportApiError('OFFLINE', 'Upload to storage failed')),
        ),
      );
  }

  /* ----------------------------- Issue reports ----------------------------- */

  /** Nudges online right now on the target, for the "stop a nudge" picker. */
  issueReportNudges(target?: string): Observable<IssueReportNudgesResponse> {
    return this.get('/issue-reports/nudges', target ? { target } : {});
  }

  /** Like `reserveAttachment`, for a report (no chat). Unused ones are discarded. */
  reserveIssueReportAttachment(file: File): Observable<SupportApiAttachmentReservation> {
    return this.post('/issue-reports/attachments', {
      name: file.name,
      size: file.size,
      mimeType: file.type,
    });
  }

  /**
   * Sends the report: turns the chosen nudges off, opens a ticket and emails
   * the client. Repeating the same `clientRequestId` returns the first answer.
   * Turning nudges off can take a while, hence the longer wait.
   */
  submitIssueReport(report: IssueReportRequest): Observable<IssueReportResponse> {
    return this.post('/issue-reports', report, SUPPORT_API.reportTimeoutMs);
  }

  /** A signed link to view or download a file; expires after 5 minutes. */
  attachmentUrl(chatId: string, attachmentId: string): Observable<SupportApiSignedUrl> {
    return this.get(`/chats/${chatId}/attachments/${attachmentId}/download-url`);
  }

  private get<T>(
    path: string,
    params: Record<string, string | number> = {},
  ): Observable<T> {
    return this.unwrap(
      this.http.get<SupportApiEnvelope<T>>(SUPPORT_API.basePath + path, {
        params: new HttpParams({ fromObject: params }),
      }),
    );
  }

  private post<T>(
    path: string,
    body: object,
    timeoutMs: number = SUPPORT_API.timeoutMs,
  ): Observable<T> {
    return this.unwrap(
      this.http.post<SupportApiEnvelope<T>>(SUPPORT_API.basePath + path, body),
      timeoutMs,
    );
  }

  private unwrap<T>(
    request: Observable<SupportApiEnvelope<T>>,
    timeoutMs: number = SUPPORT_API.timeoutMs,
  ): Observable<T> {
    return request.pipe(
      timeout(timeoutMs),
      map(unwrapEnvelope),
      catchError((error) => throwError(() => toSupportError(error))),
    );
  }
}
